/**
 * components/UploadGlobalConf/UploadGlobalConf.js
 * Composant global pour upload répertoire et assignation par catégories - Version File System Access API
 */
import "./UploadGlobalConf.css";

import { FileTreeRenderer } from "./FileTreeRenderer.js";
import { CategoryProcessor } from "./CategoryProcessor.js";
import { EventManager } from "./EventManager.js";

import logger from "../../utils/logger.js";

import { usageTrackingService } from "../../utils/UsageTrackingService.js";

export class UploadGlobalConf {
  constructor(options = {}) {
    // avoir le state pour sauvegarder les infos base rop dans le store
    this.state = options.state;

    this.options = {
      title: options.title || "Configuration Globale",
      description: options.description || "Déposer le répertoire parent et assigner les fichiers aux catégories.",
      onStatusChange: options.onStatusChange || null,
      onDataLoaded: options.onDataLoaded || null,
      onDataRemoved: options.onDataRemoved || null,
      ...options,
    };

    this.element = null;
    this.currentStatus = "idle";

    // Données du workflow
    this.uploadedFiles = []; // Liste des fichiers/dossiers du 1er niveau
    this.assignedItems = new Map(); // Map: fileName -> { category, status, data, subNodes }

    // Configuration des catégories
    this.categories = {
      configTiroirs: {
        name: "Configuration des Tiroirs",
        extensions: [".json"],
        isFolder: false,
        processor: "processTiroirsFile",
      },
      baseROP: {
        name: "Configuration Base ROP",
        extensions: [".xlsx", ".xls", ".xlsm"],
        isFolder: false,
        processor: "processBaseRopFile",
        subNodes: ["sheet", "column"],
      },
      rapportFiberPvReussite: {
        name: "PV Réussite - Rapport Fiber Cable Distrib",
        extensions: [".xlsx", ".xls", ".xlsm"],
        isFolder: false,
        processor: "processFiberCableFile",
      },
      pvReussite: {
        name: "Fichiers SOR des PV de Réussite",
        extensions: [],
        isFolder: true,
        processor: "processPvReussiteFiles",
      },
      rapportFiberPvEchec: {
        name: "PV d'Échec - Rapport Fiber Cable Distrib",
        extensions: [".xlsx", ".xls", ".xlsm"],
        isFolder: false,
        processor: "processFiberCableFile",
      },
      pvEchec: {
        name: "Fichiers SOR des PV d'Échec",
        extensions: [],
        isFolder: true,
        processor: "processPvEchecFiles",
      },
      pvHs: {
        name: "Fichiers SOR des Fibres Hors Service",
        extensions: [],
        isFolder: true,
        processor: "processPvHsFiles",
      },
    };

    // Initialiser les modules
    this.renderer = new FileTreeRenderer(this);
    this.processor = new CategoryProcessor(this);
    this.events = new EventManager(this);
  }

  /**
   * Mettre à jour le statut et notifier
   */
  updateStatus(status, data = null) {
    this.currentStatus = status;
    if (this.options.onStatusChange) {
      this.options.onStatusChange(status, data);
    }
  }

  /**
   * Vérifier si l'API File System Access est supportée
   */
  static isFileSystemAccessSupported() {
    return "showDirectoryPicker" in window;
  }

  /**
   * Ouvrir le sélecteur de répertoire
   */
  async openDirectoryPicker() {
    if (!UploadGlobalConf.isFileSystemAccessSupported()) {
      this.updateStatus("error", {
        message: "Votre navigateur ne supporte pas l'API File System Access. Utilisez Chrome, Edge ou Opera.",
      });
      return;
    }

    try {
      const directoryHandle = await window.showDirectoryPicker({
        mode: "read",
      });

      await this.processDirectory(directoryHandle);
    } catch (error) {
      if (error.name === "AbortError") {
        // Utilisateur a annulé - ne rien faire
        return;
      }

      logger.error(
        {
          context: "directory-picker",
          error: error.message,
          errorName: error.name,
        },
        "Erreur lors de la sélection du répertoire"
      );

      this.updateStatus("error", {
        message: `Erreur: ${error.message}`,
      });
    }
  }

  /**
   * Traiter le répertoire uploadé
   */
  async processDirectory(directoryHandle) {
    try {
      this.updateStatus("loading", { message: "Analyse du répertoire..." });

      // Extraire les éléments du premier niveau
      this.uploadedFiles = await this.extractFirstLevelItems(directoryHandle);

      if (this.uploadedFiles.length === 0) {
        throw new Error("Aucun fichier trouvé dans le répertoire");
      }

      /** @type {FileSystemAccessTrackingData} */
      const trackingData = {
        name: directoryHandle.name,
        kind: "directory",
        totalFiles: this.uploadedFiles.length,
        totalSize: this.uploadedFiles.reduce((sum, item) => {
          return sum + (item.files?.reduce((s, f) => s + f.size, 0) || 0);
        }, 0),
        items: this.uploadedFiles.map((item) => ({
          name: item.name,
          type: item.type,
          filesCount: item.files?.length || 0,
          size: item.files?.reduce((sum, f) => sum + f.size, 0) || 0,
          extensions: item.files
            ? [
                ...new Set(
                  item.files.map((f) => {
                    const ext = f.name.lastIndexOf(".");
                    return ext > 0 ? f.name.substring(ext).toLowerCase() : "";
                  })
                ),
              ].filter((e) => e)
            : [],
        })),
      };

      usageTrackingService.trackUpload([trackingData]).catch((err) => {
        logger.warn(
          {
            context: "upload-tracking-failed",
            error: err.message,
          },
          "Tracking upload échoué (non-bloquant)"
        );
      });

      this.renderer.renderFileTree();
      this.events.bindTreeEvents();

      this.updateStatus("success", {
        message: `${this.uploadedFiles.length} éléments trouvés`,
        count: this.uploadedFiles.length,
      });
    } catch (error) {
      logger.error(
        {
          context: "directory-processing",
          error: error.message,
          stack: error.stack,
          fileCount: this.uploadedFiles?.length || 0,
        },
        "❌ UploadGlobalConf: Erreur traitement répertoire"
      );
      this.resetDisplay();
      this.updateStatus("error", { message: error.message, error: error });
    }
  }

  /**
   * Extraire les éléments du premier niveau avec File System Access API
   */
  async extractFirstLevelItems(directoryHandle) {
    const items = [];

    for await (const entry of directoryHandle.values()) {
      if (entry.kind === "directory") {
        // Parcourir le dossier pour compter les fichiers
        const files = [];
        for await (const subEntry of entry.values()) {
          if (subEntry.kind === "file") {
            const file = await subEntry.getFile();

            // Filtrer les fichiers temporaires Excel
            if (file.name.startsWith("~$")) {
              continue;
            }

            // Vérifier l'extension
            const extension = this.getFileExtension(file.name);
            const allowedExtensions = [".json", ".xlsx", ".xls", ".xlsm", ".sor"];

            if (allowedExtensions.includes(extension)) {
              files.push(file);
            }
          }
        }

        // IMPORTANT : Ajouter le dossier MÊME S'IL EST VIDE
        items.push({
          name: entry.name,
          type: "folder",
          path: entry.name,
          extension: null,
          files: files,
          directoryHandle: entry,
        });
      } else if (entry.kind === "file") {
        const file = await entry.getFile();

        // Filtrer les fichiers temporaires Excel
        if (file.name.startsWith("~$")) {
          continue;
        }

        // Vérifier l'extension
        const extension = this.getFileExtension(file.name);
        const allowedExtensions = [".json", ".xlsx", ".xls", ".xlsm", ".sor"];

        if (allowedExtensions.includes(extension)) {
          items.push({
            name: entry.name,
            type: "file",
            path: entry.name,
            extension: extension,
            files: [file],
            fileHandle: entry,
            originalFile: file,
          });
        }
      }
    }

    return items;
  }

  /**
   * Obtenir l'extension d'un fichier
   */
  getFileExtension(fileName) {
    const lastDot = fileName.lastIndexOf(".");
    return lastDot > 0 ? fileName.substring(lastDot).toLowerCase() : "";
  }

  /**
   * Obtenir les catégories compatibles pour un élément
   */
  getCompatibleCategories(item) {
    const compatible = [];

    Object.entries(this.categories).forEach(([key, category]) => {
      if (item.type === "file" && !category.isFolder) {
        // Fichier : vérifier extension
        if (category.extensions.length === 0 || category.extensions.includes(item.extension)) {
          compatible.push({ key, ...category });
        }
      } else if (item.type === "folder" && category.isFolder) {
        // Dossier : catégories dossier
        compatible.push({ key, ...category });
      }
    });

    return compatible;
  }

  /**
   * Assigner un élément à une catégorie
   */
  async assignItemToCategory(itemName, categoryKey) {
    const item = this.uploadedFiles.find((f) => f.name === itemName);
    const category = this.categories[categoryKey];

    if (!item || !category) {
      logger.error(
        {
          context: "assignment-validation",
          itemName,
          categoryKey,
          availableItems: this.uploadedFiles.map((f) => f.name),
          availableCategories: Object.keys(this.categories),
        },
        "Élément ou catégorie introuvable"
      );
      return;
    }

    try {
      // Marquer comme en traitement
      this.assignedItems.set(itemName, {
        category: categoryKey,
        categoryName: category.name,
        status: "processing",
        data: null,
        subNodes: {},
      });

      this.renderer.renderFileTree();
      this.events.bindTreeEvents();
      this.updateStatus("loading", { message: `Traitement de ${itemName}...` });

      // Traitement selon la catégorie via le processeur
      await this.processor.processItemByCategory(item, categoryKey);
    } catch (error) {
      logger.error(
        {
          context: "item-processing",
          itemName,
          categoryKey,
          error: error.message,
          stack: error.stack,
        },
        `❌ Erreur traitement ${itemName}`
      );

      // Marquer comme erreur
      this.assignedItems.set(itemName, {
        ...this.assignedItems.get(itemName),
        status: "error",
        error: error.message,
      });

      this.renderer.renderFileTree();
      this.events.bindTreeEvents();
      this.updateStatus("error", { message: `Erreur: ${error.message}` });
    }
  }

  /**
   * Exclure un élément de la liste (le retirer définitivement)
   */
  excludeItem(itemName) {
    // Retirer de la liste des fichiers uploadés
    this.uploadedFiles = this.uploadedFiles.filter((item) => item.name !== itemName);

    // Re-render
    this.renderer.renderFileTree();
    this.events.bindTreeEvents();

    this.updateStatus("success", { message: `${itemName} exclu de la liste` });
  }

  /**
   * Libérer un élément assigné (le remettre en non-assigné)
   */
  unassignItem(itemName) {
    const assigned = this.assignedItems.get(itemName);
    if (!assigned) return;

    // Supprimer l'assignation
    this.assignedItems.delete(itemName);

    // Nettoyer le state global si callback disponible
    if (this.options.onDataRemoved) {
      this.options.onDataRemoved(assigned.category);
    }

    // Re-render
    this.renderer.renderFileTree();
    this.events.bindTreeEvents();

    this.updateStatus("success", { message: `${itemName} libéré` });
  }

  /**
   * Template état initial upload
   */
  renderInitialState() {
    return `
    <div class="global-upload-content">
      <div class="upload-left">
        <div class="global-upload-icon">📁</div>
        <div class="global-upload-text">
          <p class="global-upload-title">Importez votre dossier de livraison</p>
          <p class="global-upload-subtitle">Cliquez pour sélectionner le dossier parent</p>
        </div>
      </div>
      <div class="upload-right">
        <button class="btn btn-primary global-upload-btn">
          📂 Choisir un répertoire
        </button>
      </div>
    </div>
  `;
  }

  /**
   * Réinitialiser l'affichage
   */
  resetDisplay() {
    this.uploadedFiles = [];
    this.assignedItems.clear();

    if (this.element) {
      this.renderer.renderFileTree();

      const uploadSection = this.element.querySelector(".upload-section");
      if (uploadSection) {
        uploadSection.innerHTML = `
          <div class="upload-area">
            ${this.renderInitialState()}
          </div>
        `;
        this.events.bindUploadEvents();
      }
    }
  }

  /**
   * Rendu principal
   */
  render() {
    return `
      <div class="upload-global-conf">
        <!-- Upload & Tree -->
        <div class="main-column">
          <div class="section-header">
            <p class="section-description">${this.options.description}</p>
          </div>
          
          <!-- Section Upload -->
          <div class="upload-section">
            <div class="upload-area">
              ${this.renderInitialState()}
            </div>
          </div>

          <!-- Arbre des fichiers -->
          <div class="file-tree-section">
            <h4 class="tree-section-title">Fichiers et dossiers détectés</h4>
            <div class="file-tree-container">
              <div class="tree-empty">En attente du répertoire...</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Monter le composant
   */
  mount(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`UploadGlobalConf container ${selector} not found`);
    }

    container.innerHTML = this.render();
    this.element = container.querySelector(".upload-global-conf");
    this.events.bindUploadEvents();
    this.updateStatus("idle");
  }

  /**
   * Démonter le composant
   */
  unmount() {
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
    this.resetDisplay();
    this.updateStatus("idle");
  }

  /**
   * Réinitialiser le composant
   */
  reset() {
    this.resetDisplay();
    this.updateStatus("idle");
  }

  /**
   * Obtenir les éléments assignés
   */
  getAssignedItems() {
    return Object.fromEntries(this.assignedItems);
  }

  /**
   * Vérifier si tous les éléments requis sont traités
   */
  isComplete() {
    const requiredCategories = Object.keys(this.categories);
    const assignedCategories = Array.from(this.assignedItems.values())
      .filter((item) => item.status === "success")
      .map((item) => item.category);

    return requiredCategories.every((cat) => assignedCategories.includes(cat));
  }
}
